import { useEffect, useCallback, useRef, useMemo } from 'react'
import { functionsClient } from '../../../lib/functionsClient'
import type { UploadItem } from '../types'
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch'
import {
  addToQueue,
  updateItemStatus,
  setProcessing,
  type UploadItemMetadata,
} from '../../../store/slices/uploadQueueSlice'

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

export interface UseDocumentUploadResult {
  uploadQueue: UploadItem[]
  handleFilesSelected: (files: File[]) => Promise<void>
  isUploading: boolean
  totalDocs: number
  uploadedCount: number
  completedUploads: number
}

export function useDocumentUpload(
  caseId: string | undefined,
  uploadedCount: number
): UseDocumentUploadResult {
  const dispatch = useAppDispatch()
  const allQueueMetadata = useAppSelector((state) => state.uploadQueue.items)
  const processing = useAppSelector((state) => state.uploadQueue.processing)
  
  // Store File objects in a ref Map (not serializable, so not in Redux)
  const fileMapRef = useRef<Map<string, File>>(new Map())
  const processingRef = useRef(false)

  // Filter queue items by current caseId
  const queueMetadata = useMemo(() => {
    if (!caseId) return []
    return allQueueMetadata.filter((item) => item.caseId === caseId)
  }, [allQueueMetadata, caseId])

  // Merge Redux metadata with File objects from Map ref (filtered by caseId)
  const uploadQueue: UploadItem[] = useMemo(() => {
    return queueMetadata.map((metadata) => {
      const file = fileMapRef.current.get(metadata.id)
      if (!file) {
        // File not found - this shouldn't happen, but handle gracefully
        console.warn(`File not found for upload item ${metadata.id}`)
        // Create a dummy File object to maintain type safety
        const dummyFile = new File([], metadata.fileName)
        return {
          ...metadata,
          file: dummyFile,
        }
      }
      return {
        ...metadata,
        file,
      }
    })
  }, [queueMetadata])

  // Process upload queue
  const processUploadQueue = useCallback(async () => {
    if (processingRef.current || processing || !caseId) return
    
    processingRef.current = true
    dispatch(setProcessing(true))

    const pending = queueMetadata.filter((i) => i.status === 'pending')

    for (const item of pending) {
      const file = fileMapRef.current.get(item.id)
      if (!file) {
        console.warn(`File not found for upload item ${item.id}`)
        continue
      }

      // Update status to uploading
      dispatch(
        updateItemStatus({
          id: item.id,
          status: 'uploading',
          progress: 0.5,
        })
      )

      try {
        const result = await functionsClient.uploadFile(file, caseId)

        if (!result.success) {
          throw new Error('Upload failed')
        }

        // Update status to complete
        dispatch(
          updateItemStatus({
            id: item.id,
            status: 'complete',
            progress: 1.0,
          })
        )
      } catch (error) {
        // Update status to error
        dispatch(
          updateItemStatus({
            id: item.id,
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed',
          })
        )
      }
    }

    processingRef.current = false
    dispatch(setProcessing(false))
  }, [caseId, queueMetadata, processing, dispatch])

  // Process queue when pending items are available
  useEffect(() => {
    const hasPending = queueMetadata.some((i) => i.status === 'pending')
    if (hasPending && !processingRef.current) {
      processUploadQueue()
    }
  }, [queueMetadata, processUploadQueue])

  // Handle file selection
  const handleFilesSelected = useCallback(
    async (files: File[]): Promise<void> => {
      if (!caseId) return

      const validFiles = files.filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          console.warn(`${file.name} exceeds 25MB limit`)
          return false
        }
        return true
      })

      const newItems: UploadItemMetadata[] = validFiles.map((file) => {
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        // Store File object in Map ref
        fileMapRef.current.set(id, file)
        return {
          id,
          caseId,
          fileName: file.name,
          status: 'pending' as const,
          progress: 0,
        }
      })

      dispatch(addToQueue(newItems))
    },
    [caseId, dispatch]
  )

  // Clean up File objects when items are removed from queue
  // Clean up for all cases, not just current caseId
  useEffect(() => {
    const currentIds = new Set(allQueueMetadata.map((item) => item.id))
    // Remove File objects for items no longer in queue
    for (const [id] of fileMapRef.current.entries()) {
      if (!currentIds.has(id)) {
        fileMapRef.current.delete(id)
      }
    }
  }, [allQueueMetadata])

  const pendingUploads = queueMetadata.filter(
    (i) => i.status === 'pending' || i.status === 'uploading'
  ).length
  const completedUploads = queueMetadata.filter(
    (i) => i.status === 'complete'
  ).length
  const isUploading = pendingUploads > 0
  const totalDocs = uploadedCount + completedUploads

  return {
    uploadQueue,
    handleFilesSelected,
    isUploading,
    totalDocs,
    uploadedCount,
    completedUploads,
  }
}

